import type { Object3D } from "three";
import type { PointerGesturePressDetail } from "../../../../input/PointerGestureInterpreter";
import type { Engine } from "../../../../engine/Engine";
import type { PlanarGraphController } from "../../../../shape/segment/PlanarGraphController";
import { raycast } from "../../../../physics/PhysicsUtil";
import type { TargetCandidate } from "../TargetCandidate";
import type { SegmentInteractionTarget } from "../../segment/interaction/DesignerTarget";
import type { DesignerObject } from "../../object/DesignerObject";
import type { SnapKind } from "../../../../snap/SnapEngine";
import type { XzPoint } from "../../../../shape/segment/types";
import { pointerXzOnFloor } from "../../segment/tools/planePointer";
import { segmentIdFromObject } from "../../segment/tools/openingDragPointer";

/**
 * 3D-only picker for door/window fixtures (attached segments).
 * Uses PlanarGraph3DView pickTargets — not the floor-plane segment tool path.
 */
export class Opening3DTargetPicker {
  constructor(
    private readonly _segments: PlanarGraphController,
    private readonly _engine: Engine,
    private readonly _createObject: (
      target: SegmentInteractionTarget,
      raw: XzPoint,
      snapped: XzPoint,
      snappedKinds: SnapKind[],
    ) => DesignerObject,
  ) {}

  pick(event: PointerGesturePressDetail): TargetCandidate | null {
    const targets = this._segments.pickTargets3D;
    if (!targets.length) return null;
    const intersections = raycast(
      event as any,
      targets as Object3D[],
      this._engine.activeCamera?.instance,
      this._engine.canvas,
    );
    if (!intersections?.length) return null;

    for (const hit of intersections) {
      const segmentId = segmentIdFromObject(hit.object);
      if (!segmentId) continue;
      const entry = this._segments.planarGraph.segments.find(
        (candidate) => candidate.spec.id === segmentId,
      );
      if (!entry?.spec.placement) continue;

      const raw = pointerXzOnFloor(event as any, this._engine);
      const snapped = { x: hit.point.x, z: hit.point.z };
      const projected = hit.point
        .clone()
        .project(this._engine.activeCamera!.instance);
      return {
        object: this._createObject(
          { kind: "segment", part: "body", segmentId },
          raw,
          snapped,
          [],
        ),
        depth: projected.z,
        screenDistance: 0,
        priority: 2,
      };
    }
    return null;
  }
}
